import { capsForRole, type Capability } from '@/lib/permissions';
import { isOn, type Features } from '@/lib/features';

/**
 * WHERE EACH ROLE MAY GO, AND WHAT ITS MENU HOLDS.
 *
 * THE INTERVIEWER'S DOOR. An Interviewer sees only the interviews they are asked to join
 * (owner's decision 15; plan "roles"). The server is the lock — authenticate() answers an
 * Interviewer 'not found' on every staff route — and the app mirrors it with ONE allow-list
 * of addresses, applied by one wrapper around /admin/* (App.tsx RoleGate): any other
 * address sends them to My interviews before the page, or anything it would fetch, is
 * loaded. Capabilities are not used for this on purpose: INTERVIEWER has none, and a
 * capability added later for staff cannot open a staff page to it by accident.
 *
 * The router matches paths case-insensitively and ignores one trailing slash, so the list
 * does too — in both directions. A capitalised "/Admin/My-Interviews/" must not loop, and
 * nothing that merely starts like an allowed address may pass: each pattern is anchored at
 * both ends, and an encoded slash or a dot segment matches none of them. Anything the list
 * refuses is refused, never "probably fine".
 *
 * Staff are not affected here: their pages keep today's capability gates.
 */

export const STAFF_HOME = '/admin/dashboard';
export const INTERVIEWER_HOME = '/admin/my-interviews';
export const ACCOUNT_PAGE = '/admin/account';

/**
 * Every address an Interviewer may open. Append here — and nowhere else — when the interview
 * stage adds a page for them, with a test in roleRouting.test.ts.
 */
export const INTERVIEWER_PAGES: readonly RegExp[] = [
  /^\/admin\/my-interviews\/?$/i,
  // The interview page, one round at a time (plan: /admin/interviews/rounds/:roundId).
  /^\/admin\/interviews\/rounds\/[a-z0-9_-]+\/?$/i,
  // Their own account: details and Change password.
  /^\/admin\/account\/?$/i,
];

export const isInterviewer = (role?: string | null): boolean => role === 'INTERVIEWER';

/**
 * Where a role lands: sign-in, the root, the 404, and any refused page.
 *
 * `gateResults` is true once the result pages ask for "view results" (with Interviews on):
 * an account that has no such capability — no current staff role is one — then lands on its
 * account page rather than on a dashboard it would be sent away from.
 */
export function homeFor(role?: string | null, gateResults = false): string {
  if (isInterviewer(role)) return INTERVIEWER_HOME;
  if (gateResults && !capsForRole(role ?? undefined).includes('viewResults')) return ACCOUNT_PAGE;
  return STAFF_HOME;
}

/** Whether this role may open this path (the pathname only: no query, no hash). */
export function mayOpen(role: string | null | undefined, pathname: string): boolean {
  if (!isInterviewer(role)) return true;
  return INTERVIEWER_PAGES.some((page) => page.test(pathname));
}

// ── The menu ────────────────────────────────────────────────────────────────────

export interface NavRule {
  href: string;
  label: string;
  /** Who sees it, given the role's capabilities and the switches. */
  shows: (ctx: { role?: string | null; can: (c: Capability) => boolean; features: Features }) => boolean;
}

/**
 * With Interviews on, Dashboard, Jobs, Candidates and Analytics ask for "view results"
 * (plan: "which every current role has, so nothing visible changes"). With it off they show
 * to every staff role, as today.
 */
const results = (ctx: Parameters<NavRule['shows']>[0]) =>
  !isOn(ctx.features, 'INTERVIEWS_ENABLED') || ctx.can('viewResults');

/**
 * The menu, in order. An Interviewer is handled before these rules: they get My interviews
 * and nothing else, whatever is switched on.
 *
 * 'Interviews' is the board (/admin/interviews): staff who view results — HR, and Hiring
 * Managers, who reject and decide there (decision 5) — with Interviews on. It sits after
 * Candidates, the next step of the same pipeline.
 */
export const NAV_RULES: readonly NavRule[] = [
  { href: '/admin/dashboard', label: 'Dashboard', shows: results },
  { href: '/admin/positions', label: 'Jobs', shows: results },
  { href: '/admin/library', label: 'Test Library', shows: (c) => c.can('manageLibrary') },
  { href: '/admin/candidates', label: 'Candidates', shows: results },
  { href: '/admin/interviews', label: 'Interviews', shows: (c) => isOn(c.features, 'INTERVIEWS_ENABLED') && c.can('viewResults') },
  { href: '/admin/analytics', label: 'Analytics', shows: results },
  // Every panelist's list: staff sit on panels too (plan "frontend"). The page itself is
  // the Interviewer's home; for staff it appears with Interviews.
  { href: INTERVIEWER_HOME, label: 'My interviews', shows: (c) => isOn(c.features, 'INTERVIEWS_ENABLED') && c.can('viewResults') },
  // Owners and Admins as today; HR too once "Team rules" lets them add Interviewers.
  { href: '/admin/team', label: 'Team', shows: (c) => c.can('manageTeam') || (c.can('addInterviewers') && isOn(c.features, 'TEAM_RULES')) },
  { href: '/admin/settings', label: 'Settings', shows: (c) => c.can('manageSettings') },
];

/** The hrefs this role's menu shows, in order. */
export function navFor(role: string | null | undefined, features: Features): string[] {
  if (isInterviewer(role)) return [INTERVIEWER_HOME];
  const caps = capsForRole(role ?? undefined);
  const can = (c: Capability) => caps.includes(c);
  return NAV_RULES.filter((r) => r.shows({ role, can, features })).map((r) => r.href);
}

/** The pathname of an in-app path that may carry a query or a hash. */
export const pathOnly = (path: string): string => path.split(/[?#]/, 1)[0];
